"use client";
import Image from "next/image";
import { useRef } from "react";
import { corporateTraining } from "@assets";
import { Button } from "@components/ui/button";
import { motion, useInView } from "framer-motion";
import { Card, CardContent, CardHeader } from "@components/ui/card";

const CorporateTraining = () => {
	const titleRef = useRef(null);
	const isTitleInView = useInView(titleRef, { once: true, margin: "-100px" });
	const mobileImageRef = useRef(null);
	const isMobileImageInView = useInView(mobileImageRef, { once: true, margin: "-100px" });
	const desktopImageRef = useRef(null);
	const isDesktopImageInView = useInView(desktopImageRef, { once: true, margin: "-100px" });

	return (
		<section className="bg-accent w-full px-4 sm:px-6 lg:px-8">
			<div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center py-12">
				<motion.div animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }} className="flex flex-col items-center gap-2 text-center" initial={{ opacity: 0, y: 30 }} ref={titleRef} transition={{ duration: 0.8 }}>
					<h2 className="text-3xl font-bold sm:text-4xl">{"Corporate Training"}</h2>
					<div className="flex flex-col items-center">
						<motion.p animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} className="text-muted-foreground mx-auto max-w-2xl px-2 text-sm leading-relaxed sm:text-base md:text-lg" initial={{ opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.2 }}>
							{"Designed for teams, built for impact."}
						</motion.p>
						<motion.p animate={isTitleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }} className="text-muted-foreground mx-auto max-w-2xl px-2 text-sm leading-relaxed sm:text-base md:text-lg" initial={{ opacity: 0, y: 20 }} transition={{ duration: 0.6, delay: 0.4 }}>
							{"Give your employees the skills they need to lead, grow, and innovate"}
						</motion.p>
					</div>
				</motion.div>
				<div className="relative w-full max-w-5xl py-12">
					<div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-3 lg:hidden">
						<motion.div animate={isMobileImageInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }} className="mx-auto" initial={{ opacity: 0, x: -50 }} ref={mobileImageRef} transition={{ duration: 0.8 }}>
							<Image alt="Corporate Training Scenes" className="rounded-xl object-cover" src={corporateTraining} />
						</motion.div>
						<motion.div animate={isMobileImageInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }} className="mx-auto" initial={{ opacity: 0, y: 50 }} transition={{ duration: 0.8, delay: 0.3 }}>
							<Card className="bg-secondary text-secondary-foreground max-w-xl gap-6 rounded-xl border-0 p-8 shadow-xl">
								<CardHeader className="text-2xl leading-tight font-bold lg:text-3xl">{"Upskill Your Workforce with Custom Corporate Training"}</CardHeader>
								<CardContent className="text-base leading-relaxed lg:text-lg">
									{"Empower your team with industry-ready learning modules designed for real business impact. From leadership to technical skills, our corporate programs are built to scale with your team's growth. Track progress, drive results, and foster a culture of continuous learning."}
								</CardContent>
								<Button className="text-secondary bg-secondary-foreground hover:bg-secondary-foreground/80 hover:text-secondary/80 w-full max-w-fit rounded-full font-semibold transition-colors duration-300" size="lg" variant="ghost">
									{"Request a Demo"}
								</Button>
							</Card>
						</motion.div>
					</div>
					<div className="relative hidden w-full max-w-5xl py-12 lg:block">
						<motion.div animate={isDesktopImageInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }} initial={{ opacity: 0, x: -100 }} ref={desktopImageRef} transition={{ duration: 1 }}>
							<Image alt="Corporate Training Scenes" className="object-cover" src={corporateTraining} />
						</motion.div>
						<motion.div animate={isDesktopImageInView ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, x: 100, y: 50 }} className="absolute top-1/2 right-1/6 translate-x-1/4 -translate-y-1/2 transform" initial={{ opacity: 0, x: 100, y: 50 }} transition={{ duration: 0.8, delay: 0.5 }}>
							<Card className="bg-secondary text-secondary-foreground w-full max-w-lg gap-6 rounded-xl border-0 p-8 shadow-xl">
								<CardHeader className="text-2xl leading-tight font-bold lg:text-3xl">{"Upskill Your Workforce with Custom Corporate Training"}</CardHeader>
								<CardContent className="text-base leading-relaxed lg:text-lg">
									{"Empower your team with industry-ready learning modules designed for real business impact. From leadership to technical skills, our corporate programs are built to scale with your team's growth. Track progress, drive results, and foster a culture of continuous learning."}
								</CardContent>
								<Button className="text-secondary bg-secondary-foreground hover:bg-secondary-foreground/80 hover:text-secondary/80 w-full max-w-fit rounded-full font-semibold transition-colors duration-300" size="lg" variant="ghost">
									{"Request a Demo"}
								</Button>
							</Card>
						</motion.div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default CorporateTraining;
